import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import {
  TrendingUp, TrendingDown, AlertTriangle, Boxes, Plus, Receipt, ShoppingCart, ClipboardList,
  Trophy, Activity, Clock, FileSignature, CalendarClock,
} from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import api from "../api";
import { fmt, fmtNum } from "../utils";
import { useLang } from "../i18n";
import { useAuth, can } from "../context/AuthContext";

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: Math.min(i, 8) * 0.025, duration: 0.28, ease: [0.22, 1, 0.36, 1] },
  }),
};

const CountUp = ({ value, format = fmt }) => {
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) => format(v));
  useEffect(() => {
    const controls = animate(mv, Number(value) || 0, { duration: 0.28, ease: "easeOut" });
    return controls.stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <motion.span>{display}</motion.span>;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const target = new Date(dateStr.length <= 7 ? `${dateStr}-01` : dateStr);
  return Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24));
};

const timeAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso)) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.round(hrs / 24)} d ago`;
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

const Kpi = ({ testid, label, value, icon: Icon, tone, index, format = fmt }) => (
  <motion.div
    data-testid={testid} custom={index} variants={fadeUp} initial="hidden" animate="show"
    whileHover={{ y: -2 }}
    className="sgp-kpi-card flex items-start justify-between"
  >
    <div>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className={`mt-2 text-2xl font-heading font-bold font-mono tracking-tight ${tone || "text-slate-900"}`}>
        <CountUp value={value} format={format} />
      </p>
    </div>
    <div className="h-11 w-11 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center shadow-sm">
      <Icon className="h-5 w-5 text-teal-700" />
    </div>
  </motion.div>
);

const WidgetCard = ({ title, icon: Icon, index, children, testid, accent, span = "lg:col-span-3" }) => (
  <motion.div data-testid={testid} custom={index} variants={fadeUp} initial="hidden" animate="show" className={`sgp-card p-5 ${span}`}>
    <h3 className="sgp-panel-title flex items-center gap-2 mb-4">
      {Icon && <Icon className={`h-4 w-4 ${accent || "text-teal-700"}`} />} {title}
    </h3>
    {children}
  </motion.div>
);

const snapshotKey = (user, workspace) => (
  user?.email && user?.role && workspace?.company?.id && workspace?.financial_year
    ? `sgp_dashboard_snapshot:${user.email.toLowerCase()}:${user.role}:${workspace.company.id}:${workspace.financial_year}`
    : ""
);

function cachedDashboard(user, workspace) {
  const key = snapshotKey(user, workspace);
  if (!key) return null;
  try {
    const cached = JSON.parse(sessionStorage.getItem(key) || "null");
    return cached?.data || null;
  } catch {
    return null;
  }
}

function StockAlerts({ s }) {
  return (
    <div className="space-y-2 text-sm">
      <div className="flex justify-between"><span className="text-slate-500">Low stock</span>
        <span className="font-mono font-bold text-amber-600">{s.low_stock ?? "—"}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">Expiring batches</span>
        <span className="font-mono font-bold text-rose-600">{s.expiring_batches ?? "—"}</span></div>
      <div className="flex justify-between"><span className="text-slate-500">Expired batches</span>
        <span className="font-mono font-bold text-rose-700">{s.expired_batches ?? "—"}</span></div>
      {s.stock_value != null && (
        <div className="flex justify-between border-t border-slate-100 pt-2"><span className="text-slate-500">Stock value</span>
          <span className="font-mono font-bold text-slate-800">{fmt(s.stock_value)}</span></div>
      )}
    </div>
  );
}

function TenderDeadlines({ rows = [] }) {
  if (!rows.length) return <p className="text-sm text-slate-400 py-6 text-center">No open tender deadlines.</p>;
  return (
    <div className="space-y-2">
      {rows.map((td) => {
        const d = daysUntil(td.opening_date || td.submission_date);
        const urgent = d !== null && d <= 3;
        return (
          <Link key={td.id} to={`/tenders/${td.id}`} className="flex items-center justify-between gap-2 rounded-md px-2.5 py-2 hover:bg-slate-50 transition-colors">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{td.tender_no}</p>
              <p className="text-[11px] text-slate-500 truncate">{td.department} · {td.status}</p>
            </div>
            {d !== null && (
              <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full ${urgent ? "bg-rose-100 text-rose-700" : "bg-teal-50 text-teal-700"}`}>
                {d >= 0 ? `${d}d` : "overdue"}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

function ExpiryRadar({ rows = [] }) {
  if (!rows.length) return <p className="text-sm text-slate-400 py-6 text-center">No batches expiring soon.</p>;
  return (
    <div className="space-y-2">
      {rows.map((b, i) => {
        const d = daysUntil(b.expiry);
        const tone = d !== null && d <= 30 ? "text-rose-600" : d !== null && d <= 90 ? "text-amber-600" : "text-slate-500";
        return (
          <div key={i} className="flex items-center justify-between gap-2 text-sm">
            <div className="min-w-0">
              <p className="font-medium text-slate-800 truncate">{b.item_name}</p>
              <p className="text-[11px] text-slate-500">Batch {b.batch_no || "-"} · Qty {b.qty}</p>
            </div>
            <span className={`shrink-0 font-mono text-xs font-bold ${tone}`}>{b.expiry}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { t } = useLang();
  const { user, workspace } = useAuth();
  const [s, setS] = useState(() => cachedDashboard(user, workspace));
  const [summaryError, setSummaryError] = useState("");
  const [activity, setActivity] = useState(null);

  const financialView = can(user, "accounts", "view");
  const tenderView = can(user, "tender", "view");
  const stockView = can(user, "stock", "view");
  const mastersView = can(user, "masters", "view");
  const purchaseView = can(user, "purchase", "view");
  const invoiceView = can(user, "invoice", "view");
  const documentView = can(user, "documents", "add");
  const canSeeActivity = user?.role === "Admin";

  const loadSummary = useCallback(() => {
    if (!user) return;
    setSummaryError("");
    api.get("/reports/summary")
      .then((r) => {
        setS(r.data);
        const key = snapshotKey(user, workspace);
        if (key) {
          try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), data: r.data })); } catch { /* ignore */ }
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          const key = snapshotKey(user, workspace);
          if (key) {
            try { sessionStorage.removeItem(key); } catch { /* ignore */ }
          }
          setS(null);
          setSummaryError(err?.response?.data?.detail || err.message || "Dashboard access changed. Please sign in again.");
          return;
        }
        setS((current) => {
          if (!current) setSummaryError(err.message || "Dashboard could not be loaded.");
          return current;
        });
      });
  }, [user, workspace]);

  useEffect(() => {
    setS(cachedDashboard(user, workspace));
    loadSummary();
  }, [user, workspace, loadSummary]);

  useEffect(() => {
    if (!canSeeActivity) return;
    api.get("/change-log", { params: { limit: 6 } }).then((r) => setActivity(r.data)).catch(() => setActivity([]));
  }, [canSeeActivity, workspace]);

  const quickTop = useMemo(() => {
    const actions = [];
    if (can(user, "purchase", "full")) actions.push({ to: "/purchases/new", label: t("newPurchase"), icon: ShoppingCart, dark: true, testid: "quick-new-purchase" });
    if (can(user, "invoice", "full")) actions.push({ to: "/sales/new", label: t("newSale"), icon: Receipt, primary: true, testid: "quick-new-sale" });
    if (can(user, "quotation", "full")) actions.push({ to: "/quotations/new", label: t("newQuotation"), icon: ClipboardList, testid: "quick-new-quotation" });
    if (can(user, "documents", "add")) actions.push({ to: "/letters/new", label: "New Letter", icon: FileSignature, testid: "quick-new-letter" });
    return actions.slice(0, 3);
  }, [user, t]);

  if (summaryError) return (
    <div data-testid="dashboard-error" className="py-20 text-center space-y-3">
      <p className="text-sm font-semibold text-rose-700">Dashboard could not be loaded.</p>
      <p className="text-xs text-slate-500">{summaryError}</p>
      <button type="button" onClick={loadSummary}
        className="text-xs font-semibold px-3.5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800">Try again</button>
    </div>
  );
  if (!s) return <div data-testid="dashboard-loading" className="text-sm text-slate-500 py-20 text-center">Loading dashboard...</div>;

  const trendWithMargin = (s.trend || []).map((row) => ({ ...row, margin: row.sales - row.purchase }));
  const ageingTotal = Object.values(s.receivables_ageing || {}).reduce((a, b) => a + b, 0) || 1;
  const ageingBuckets = [
    { key: "0_30", label: "0–30 days", color: "#0D9488" },
    { key: "30_60", label: "30–60 days", color: "#F59E0B" },
    { key: "60_plus", label: "60+ days", color: "#DC2626" },
  ];
  const rm = s.role_metrics || {};

  return (
    <div data-testid="dashboard" className="sgp-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }} className="sgp-page-header">
        <div>
          <p className="sgp-page-eyebrow">Operational command center</p>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="sgp-page-title">{greeting()}, {user?.name?.split(" ")[0] || t("dashboard")}</h1>
            <span className="sgp-status-chip border-teal-100 bg-teal-50 text-teal-700">{user?.role}</span>
          </div>
          <p className="sgp-page-subtitle">{s.company?.trade_name || workspace?.company?.name || "Company"} · {workspace?.financial_year || ""} · role-specific workspace · {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          {quickTop.map(({ to, label, icon: Icon, dark, primary, testid }) => (
            <Link key={to} to={to} data-testid={testid}
              className={`${dark ? "inline-flex h-10 items-center gap-2 rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white shadow-sm hover:bg-slate-800 transition-all" : primary ? "sgp-primary-btn text-xs" : "sgp-secondary-btn text-xs"}`}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </Link>
          ))}
        </div>
      </motion.div>

      {financialView ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi index={0} testid="kpi-total-sales" label={t("totalSales")} value={s.total_sales} icon={TrendingUp} tone="text-teal-700" />
          <Kpi index={1} testid="kpi-total-purchases" label={t("totalPurchases")} value={s.total_purchase} icon={TrendingDown} />
          <Kpi index={2} testid="kpi-receivable" label={t("receivable")} value={s.receivable} icon={Receipt} tone="text-emerald-700" />
          <Kpi index={3} testid="kpi-payable" label={t("payable")} value={s.payable} icon={AlertTriangle} tone="text-rose-600" />
        </div>
      ) : tenderView ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi index={0} label="Active Tenders" value={rm.active_tenders} icon={Trophy} format={fmtNum} tone="text-teal-700" />
          <Kpi index={1} label="Due in 7 days" value={rm.tenders_due_7d} icon={CalendarClock} format={fmtNum} tone="text-rose-600" />
          <Kpi index={2} label="Open Quotations" value={rm.open_quotations} icon={ClipboardList} format={fmtNum} />
          <Kpi index={3} label="Low Stock" value={rm.low_stock} icon={Boxes} format={fmtNum} tone="text-amber-600" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Kpi index={0} label="Recent Invoices" value={rm.recent_invoice_count} icon={Receipt} format={fmtNum} />
          <Kpi index={1} label="Draft Letters" value={rm.draft_letters} icon={FileSignature} format={fmtNum} />
          <Kpi index={2} label="Low Stock" value={rm.low_stock} icon={Boxes} format={fmtNum} tone="text-amber-600" />
          <Kpi index={3} label="Expiring Batches" value={rm.expiring_batches} icon={AlertTriangle} format={fmtNum} tone="text-rose-600" />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {financialView && (
          <motion.div custom={4} variants={fadeUp} initial="hidden" animate="show" className="lg:col-span-8 sgp-card p-5">
            <div className="mb-4">
              <p className="sgp-page-eyebrow">Financial pulse</p>
              <h2 className="text-lg font-heading font-bold text-slate-900">Sales vs Purchase</h2>
            </div>
            {trendWithMargin.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={trendWithMargin}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="sales" name="Sales" fill="#1D43B8" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="purchase" name="Purchase" fill="#0B1F5C" radius={[4, 4, 0, 0]} />
                  <Line dataKey="margin" name="Margin" stroke="#F5BE00" strokeWidth={2.5} dot={{ r: 3, fill: "#F5BE00" }} />
                </ComposedChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 py-16 text-center">Bills banne ke baad yahan trend dikhega.</p>}
          </motion.div>
        )}

        {stockView && (
          <WidgetCard index={5} testid="alerts-card" title="Stock Alerts" icon={AlertTriangle} accent="text-rose-600" span={financialView ? "lg:col-span-4" : "lg:col-span-4"}>
            <StockAlerts s={s} />
          </WidgetCard>
        )}

        {mastersView && (
          <WidgetCard index={6} testid="masters-card" title="Masters" icon={Boxes} span="lg:col-span-4">
            <div className="grid grid-cols-3 gap-2 text-center">
              <Link to="/companies" className="rounded-lg bg-slate-50 border border-slate-100 p-3 hover:border-teal-500">
                <p className="text-xl font-mono font-bold text-slate-900">{s.companies_count ?? "—"}</p><p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">Companies</p>
              </Link>
              <Link to="/hospitals" className="rounded-lg bg-slate-50 border border-slate-100 p-3 hover:border-teal-500">
                <p className="text-xl font-mono font-bold text-slate-900">{s.hospitals_count ?? "—"}</p><p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">Hospitals</p>
              </Link>
              <Link to="/items" className="rounded-lg bg-slate-50 border border-slate-100 p-3 hover:border-teal-500">
                <p className="text-xl font-mono font-bold text-slate-900">{s.items_count ?? "—"}</p><p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">Items</p>
              </Link>
            </div>
          </WidgetCard>
        )}

        {tenderView && (
          <WidgetCard index={7} testid="tender-deadlines-card" title="Tender Deadlines" icon={Trophy} accent="text-gold-600" span={financialView ? "lg:col-span-4" : "lg:col-span-8"}>
            <TenderDeadlines rows={s.tender_deadlines} />
          </WidgetCard>
        )}

        {tenderView && (
          <WidgetCard index={8} testid="recent-quotations-card" title="Recent Quotations" icon={ClipboardList} span="lg:col-span-4">
            {s.recent_quotations?.length ? (
              <div className="space-y-2">
                {s.recent_quotations.map((q) => (
                  <Link key={q.id} to={`/print/quotation/${q.id}`} className="block rounded-md px-2.5 py-2 hover:bg-slate-50">
                    <div className="flex justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800">{q.quote_no}</p>
                      <span className="text-[10px] text-slate-500">{q.status}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{q.hospital_name}{q.tender_ref ? ` · ${q.tender_ref}` : ""}</p>
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-6 text-center">No quotations yet.</p>}
          </WidgetCard>
        )}

        {financialView && (
          <WidgetCard index={9} testid="receivables-ageing-card" title="Receivables Ageing" icon={Clock} span="lg:col-span-4">
            <div className="space-y-3">
              {ageingBuckets.map((b) => {
                const amt = s.receivables_ageing?.[b.key] || 0;
                const pct = Math.round((amt / ageingTotal) * 100);
                return (
                  <div key={b.key}>
                    <div className="flex justify-between text-xs mb-1"><span className="text-slate-500">{b.label}</span><span className="font-mono font-semibold text-slate-700">{fmt(amt)}</span></div>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6, ease: "easeOut" }} className="h-full rounded-full" style={{ background: b.color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </WidgetCard>
        )}

        {stockView && (
          <WidgetCard index={10} testid="expiry-radar-card" title="Expiry Radar" icon={AlertTriangle} accent="text-amber-600" span={financialView ? "lg:col-span-4" : "lg:col-span-4"}>
            <ExpiryRadar rows={s.expiring_soon} />
          </WidgetCard>
        )}

        {documentView && !financialView && (
          <WidgetCard index={11} testid="recent-letters-card" title="Recent Letters" icon={FileSignature} span="lg:col-span-4">
            {s.recent_letters?.length ? (
              <div className="space-y-2">
                {s.recent_letters.map((l) => (
                  <Link key={l.id} to={`/print/letter/${l.id}`} className="block rounded-md px-2.5 py-2 hover:bg-slate-50">
                    <div className="flex justify-between gap-2"><p className="text-sm font-semibold text-slate-800">{l.ref_no}</p><span className="text-[10px] text-slate-500">{l.status}</span></div>
                    <p className="text-[11px] text-slate-500 truncate">{l.to_org || l.subject || l.letter_type}</p>
                  </Link>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-6 text-center">No letters yet.</p>}
          </WidgetCard>
        )}

        {canSeeActivity && (
          <WidgetCard index={12} testid="live-activity-card" title="Live Activity" icon={Activity} span="lg:col-span-4">
            {activity === null ? <p className="text-sm text-slate-400 py-4 text-center">Loading…</p> : activity.length ? (
              <div className="space-y-2.5">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-2 text-sm">
                    <p className="text-slate-700"><span className="font-semibold">{a.user_email}</span> {a.action}d {a.entity} <span className="text-slate-400">#{(a.entity_id || "").slice(0, 8)}</span></p>
                    <span className="shrink-0 text-[11px] text-slate-400 whitespace-nowrap">{timeAgo(a.at)}</span>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 py-6 text-center">No activity recorded yet.</p>}
          </WidgetCard>
        )}

        {invoiceView && (
          <motion.div custom={13} variants={fadeUp} initial="hidden" animate="show" className={`${purchaseView ? "lg:col-span-6" : "lg:col-span-12"} sgp-card p-5`}>
            <h3 className="text-base font-heading font-bold text-slate-800 mb-3">{t("recentBills")} — Sales</h3>
            {s.recent_sales?.length ? (
              <table className="w-full">
                <thead><tr><th className="sgp-th">Invoice</th><th className="sgp-th">Hospital</th>{financialView && <th className="sgp-th text-right">Amount</th>}</tr></thead>
                <tbody>{s.recent_sales.map((b) => (
                  <tr key={b.id}>
                    <td className="sgp-td font-mono text-xs"><Link className="text-teal-700 hover:underline" to={`/print/sale/${b.id}`}>{b.bill_no}</Link></td>
                    <td className="sgp-td">{b.hospital_name}</td>
                    {financialView && <td className="sgp-td text-right font-mono">{fmt(b.grand_total)}</td>}
                  </tr>
                ))}</tbody>
              </table>
            ) : <p className="text-sm text-slate-400 py-6 text-center">Abhi koi sales bill nahi.</p>}
          </motion.div>
        )}

        {purchaseView && (
          <motion.div custom={14} variants={fadeUp} initial="hidden" animate="show" className={`${invoiceView ? "lg:col-span-6" : "lg:col-span-12"} sgp-card p-5`}>
            <h3 className="text-base font-heading font-bold text-slate-800 mb-3">{t("recentBills")} — Purchases</h3>
            {s.recent_purchases?.length ? (
              <table className="w-full">
                <thead><tr><th className="sgp-th">Bill</th><th className="sgp-th">Company</th>{financialView && <th className="sgp-th text-right">Amount</th>}</tr></thead>
                <tbody>{s.recent_purchases.map((b) => (
                  <tr key={b.id}><td className="sgp-td font-mono text-xs">{b.bill_no}</td><td className="sgp-td">{b.company_name}</td>{financialView && <td className="sgp-td text-right font-mono">{fmt(b.grand_total)}</td>}</tr>
                ))}</tbody>
              </table>
            ) : <p className="text-sm text-slate-400 py-6 text-center">Abhi koi purchase bill nahi.</p>}
          </motion.div>
        )}
      </div>

      <motion.div custom={15} variants={fadeUp} initial="hidden" animate="show" className="sgp-toolbar">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-2">{t("quickActions")}</span>
        {[
          ["companies", "/companies", "masters"], ["hospitals", "/hospitals", "masters"], ["items", "/items", "masters"],
          ["inventory", "/stock", "stock"], ["payments", "/payments", "accounts"], ["reports", "/reports", "accounts"],
          ["tenders", "/tenders", "tender"], ["letters", "/letters", "documents"],
        ].filter(([, , module]) => can(user, module, "view")).map(([k, to]) => (
          <Link key={k} to={to} data-testid={`quick-${k}`}
            className="sgp-tab flex items-center gap-1">
            <Plus className="h-3 w-3" /> {t(k)}
          </Link>
        ))}
      </motion.div>
    </div>
  );
}
